import { Component, computed, effect, forwardRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	FormBuilder,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	catchError,
	distinctUntilChanged,
	EMPTY,
	defer,
	finalize,
	filter,
	map,
	merge,
	Observable,
	of,
	Subject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import { ENVIRONMENT } from '../../../../../../../environments/environment.interface';
import { CreateCareerStatusDynamicFields } from '../../../../../../api/app-types/career.app';
import { County } from '../../../../../../api/app-types/county.app';
import { SchoolSuggestion } from '../../../../../../api/app-types/institution.app';
import { CATEGORIES_API } from '../../../../../../api/injection-tokens/services.token';
import { INSTITUTION_SERVICE, provideInstitutionApi } from '../../../../../../api/services/institution.service';
import { LvrField } from '../../../../../../core/components/field/field';
import { LvrSearchableSelect } from '../../../../../../core/components/searchable-select/searchable-select';
import { LvrSectionForm } from '../../../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../../../core/directives/lvr-input.directive';
import { LOADING, provideLoading } from '../../../../../../core/services/loading.service';
import { hasError } from '../../../../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../../../../shared/padding/padding.directive';
import { LvrAppendAsteriskPipe } from '../../../../../../shared/pipes/append-asterisk.pipe';
import { ImmediateErrorStateMatcher } from '../../../../../../shared/validators/immediate-error-state-matcher';

@Component({
	selector: 'lvr-social-security-status-school',
	templateUrl: './social-security-status-school.html',
	styleUrl: './social-security-status-school.scss',
	imports: [
		LvrInputDirective,
		TranslocoPipe,
		LvrAppendAsteriskPipe,
		MatInput,
		MatFormFieldModule,
		MatAutocompleteModule,
		MatProgressBar,
		LvrField,
		ReactiveFormsModule,
		LvrPaddingDirective,
		LvrSearchableSelect,
		MatTooltip,
		LvrSectionForm,
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SocialSecurityStatusSchool),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => SocialSecurityStatusSchool),
			multi: true,
		},
		provideInstitutionApi,
		provideLoading,
	],
})
export class SocialSecurityStatusSchool implements ControlValueAccessor, OnInit, OnDestroy {
	private static readonly SCHOOL_SEARCH_LOADING_DELAY_MS = 250;
	public readonly matcher = new ImmediateErrorStateMatcher();
	private readonly institutionService = inject(INSTITUTION_SERVICE);
	private readonly categoriesApi = inject(CATEGORIES_API);
	public readonly env = inject(ENVIRONMENT);
	private readonly loading = inject(LOADING);
	private readonly searchPending = signal(false);
	private readonly loadingIndicatorVisible = signal(false);
	private readonly searchCompleted = signal(false);
	private readonly requestLoading = this.loading.isCurrentlyLoading(
		this.institutionService.getBranchesOfInstitution.name,
	);
	public readonly isLoadingSuggestions = computed(() => this.searchPending() || this.requestLoading());
	public readonly showLoadingSuggestions = computed(() => this.loadingIndicatorVisible());
	public readonly showNoSuggestions = computed(
		() => this.searchCompleted() && this.suggestions().length === 0 && !this.isLoadingSuggestions(),
	);
	public readonly cityDistricts = signal<County[]>([]);
	private readonly offset = this.env.pagination.offset;
	private readonly range = this.env.pagination.range;
	public readonly minMaxYear = {
		min: new Date().getFullYear() - 10,
		max: new Date().getFullYear(),
	};
	public readonly form = inject(FormBuilder).group({
		remark: ['', [Validators.maxLength(this.env.formConfiguration.career.remarkLength)]],
		schoolId: [undefined as number | null | undefined, [Validators.required]],
		careerOrientationStartYear: [
			undefined,
			[Validators.min(this.minMaxYear.min), Validators.max(this.minMaxYear.max), Validators.required],
		],
		schoolSearch: [''],
		cityDistrictId: [undefined as number | null | undefined, [Validators.required]],
	});

	private _value: number | null = null;
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onChange = (_value: CreateCareerStatusDynamicFields | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onTouched = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onValidationChange = () => {};

	private _isDisabled = false;
	public get disabled() {
		return this._isDisabled;
	}
	public suggestions = signal<SchoolSuggestion[]>([]);

	private readonly focusSearch = new Subject<string>();
	private readonly _destroy = new Subject<void>();

	public constructor() {
		effect(() => {
			this._onValidationChange();
		});

		// Fetch city districts
		effect(() => {
			this.fetchCityDistricts().then((districts) => {
				this.form.controls.cityDistrictId.enable({ emitEvent: false });
				this.cityDistricts.set(districts);
			});
		});
	}

	public ngOnInit() {
		this.registerValueChanges();
	}

	public writeValue(obj: number | null) {
		this._value = obj;
		this._onChange(obj as CreateCareerStatusDynamicFields);
		this.form.patchValue({ schoolId: obj ?? null }, { emitEvent: false });
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public registerOnChange(fn: any): void {
		this._onChange = fn;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public registerOnTouched(fn: any): void {
		this._onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this._isDisabled = isDisabled;
	}

	public ngOnDestroy(): void {
		this._destroy.next();
		this._destroy.complete();
	}

	public hasError(control: AbstractControl | null, ...errors: string[]) {
		return hasError(control, ...errors);
	}

	private registerValueChanges() {
		this.form.valueChanges.pipe(takeUntil(this._destroy)).subscribe((value) => {
			this._onChange(value as CreateCareerStatusDynamicFields);
		});

		const schoolSearchChanges = this.form.controls.schoolSearch.valueChanges.pipe(
			map((school) => (typeof school === 'string' ? school.trim() : undefined)),
			distinctUntilChanged(),
			filter((school): school is string => school !== undefined),
			map((search) => ({ search, delay: 300 })),
		);

		merge(schoolSearchChanges, this.focusSearch.pipe(map((search) => ({ search, delay: 0 }))))
			.pipe(
				takeUntil(this._destroy),
				switchMap(({ search, delay }) => {
					this.suggestions.set([]);
					this.searchCompleted.set(false);
					this.searchPending.set(true);
					return (delay ? timer(delay) : of(0)).pipe(switchMap(() => this.fetchSuggestions(search)));
				}),
			)
			.subscribe((schools) => this.suggestions.set(schools));
	}

	public displaySchool(school: SchoolSuggestion) {
		if (!school.name && this.suggestions) {
			const display = this.suggestions().find((suggestion) => suggestion.id === school.id)?.name;
			return display ?? '';
		}
		const display = school ? school.name : '';
		return display;
	}

	public schoolSelected(event: MatAutocompleteSelectedEvent) {
		const selected = event.option.value as SchoolSuggestion;
		this.form.patchValue({ schoolId: selected.id });
		this.suggestions.set([]);
		this.searchCompleted.set(false);
	}

	private fetchSuggestions(search: string): Observable<SchoolSuggestion[]> {
		const loadingId = window.crypto.randomUUID();
		const loadingIndicatorTimeout = window.setTimeout(() => {
			this.loadingIndicatorVisible.set(true);
		}, SocialSecurityStatusSchool.SCHOOL_SEARCH_LOADING_DELAY_MS);
		this.loading.setLoadingState(loadingId, this.institutionService.getBranchesOfInstitution.name);

		return defer(() =>
			this.institutionService.getBranchesOfInstitution(undefined, search, true, this.offset, this.range),
		).pipe(
			map((response) =>
				response.entities.map((school) => ({
					name: school.name,
					schoolNumber: school.schoolNumber,
					id: school.id,
				})),
			),
			tap(() => this.searchCompleted.set(true)),
			catchError((error: unknown) => {
				this.searchCompleted.set(false);
				console.error('Fehler beim Laden der Schulvorschläge', error);
				return EMPTY;
			}),
			finalize(() => {
				window.clearTimeout(loadingIndicatorTimeout);
				this.loading.clearLoadingState(loadingId);
				this.loadingIndicatorVisible.set(false);
				this.searchPending.set(false);
			}),
		);
	}

	public onInputFocus() {
		const schoolId = this.form.get('schoolId')?.value ?? '';
		const search = this.form.controls.schoolSearch.value;
		if (!schoolId && typeof search === 'string' && !search.trim()) {
			this.focusSearch.next('');
		}
	}

	// Validator interface implementation
	public validate(_control: AbstractControl): ValidationErrors | null {
		return this.form.valid ? null : { invalidForm: { valid: false, errors: this.form.errors } };
	}

	public registerOnValidatorChange(fn: () => void): void {
		this._onValidationChange = fn;
	}

	public async fetchCityDistricts() {
		const page = await this.categoriesApi.getAllCounties(this.env.pagination.offset, this.env.pagination.range);
		return page.entities;
	}
}
